{% block sellops_rules_page %}
<sw-page class="sellops-rules-page">
    {% block sellops_rules_header %}
    <template #smart-bar-header>
        <sw-button
            class="sellops-rules__back-btn"
            variant="ghost"
            size="small"
            @click="$router.push({ name: 'sellops.upsell.analytics' })"
        >
            <sw-icon
                name="regular-chevron-left-s"
                size="16px"
                class="sellops-rules__back-icon"
            />
            {{ $tc('sellops.general.back') }}
        </sw-button>
        <h2>
            {{ $tc('sellops.rules.title') }}
        </h2>
    </template>
    {% endblock %}

    {% block sellops_rules_actions %}
    <template #smart-bar-actions>
        <button
            class="sellops-rules__add-btn"
            @click="openCreateModal"
        >
            {{ $tc('sellops.rules.addRule') }}
        </button>
    </template>
    {% endblock %}

    {% block sellops_rules_content %}
    <template #content>
        <div class="sellops-rules__content">
            <div class="sellops-rules__info-banner">
                <strong>
                    {{ $tc('sellops.rules.howRulesWorkTitle') }}
                </strong>
                {{ $tc('sellops.rules.howRulesWorkBody') }}
            </div>
            <sw-loader v-if="isLoading"/>
            <div
                v-else-if="rules.length === 0"
                class="sellops-rules__empty"
            >
                <div class="sellops-rules__empty-icon">🎯</div>
                <p class="sellops-rules__empty-title">
                    {{ $tc('sellops.rules.emptyTitle') }}
                </p>
                <p class="sellops-rules__empty-body">
                    {{ $tc('sellops.rules.emptyBody') }}
                    <strong>
                        {{ $tc('sellops.rules.addRule') }}
                    </strong>                    {{ $tc('sellops.rules.emptyBody2') }}
                </p>
            </div>
            <div v-else>
                <table class="sellops-rules__table">
                    <thead>
                        <tr class="sellops-rules__thead-row">
                            <th
                                class="sellops-rules__th sellops-rules__th--priority"
                            >
                                {{ $tc('sellops.rules.colPriority') }}
                            </th>
                            <th class="sellops-rules__th">
                                {{ $tc('sellops.rules.colName') }}
                            </th>
                            <th class="sellops-rules__th">
                                {{ $tc('sellops.rules.colSalesChannel') }}
                            </th>
                            <th class="sellops-rules__th">
                                {{ $tc('sellops.rules.colTrigger') }}
                            </th>
                            <th class="sellops-rules__th">
                                {{ $tc('sellops.rules.colOffer') }}
                            </th>
                            <th
                                class="sellops-rules__th sellops-rules__th--center"
                            >
                                {{ $tc('sellops.rules.colDiscount') }}
                            </th>
                            <th
                                class="sellops-rules__th sellops-rules__th--center"
                            >
                                {{ $tc('sellops.rules.colActive') }}
                            </th>
                            <th
                                class="sellops-rules__th sellops-rules__th--right"
                            >
                                {{ $tc('sellops.rules.colActions') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(rule, index) in rules"
                            :key="rule.id"
                            :class="['sellops-rules__tbody-row', { 'sellops-rules__tbody-row--inactive': !rule.active }]"
                        >
                            <td
                                class="sellops-rules__td sellops-rules__td--priority"
                            >{{ rule.priority }}</td>
                            <td
                                class="sellops-rules__td sellops-rules__td--name"
                            >{{ rule.name }}</td>
                            <td class="sellops-rules__td">
                                <span
                                    :class="['sellops-rules__badge', (rule.sales_channel_ids || []).length ? 'sellops-rules__badge--offer' : 'sellops-rules__badge--all-channels']"
                                >{{ salesChannelsSummary(rule) }}</span>
                            </td>
                            <td class="sellops-rules__td">
                                <span
                                    class="sellops-rules__badge sellops-rules__badge--trigger"
                                >{{ triggerLabel(rule) }}</span>
                            </td>
                            <td class="sellops-rules__td">
                                <span
                                    class="sellops-rules__badge sellops-rules__badge--offer"
                                >{{ offerLabel(rule) }}</span>
                            </td>
                            <td
                                class="sellops-rules__td sellops-rules__td--discount"
                            >
                                {{ rule.discount_percent > 0 ? rule.discount_percent + '%' : '—' }}
                            </td>
                            <td
                                class="sellops-rules__td sellops-rules__td--active"
                            >
                                <input
                                    type="checkbox"
                                    class="sellops-rules__toggle"
                                    :checked="rule.active"
                                    @change="toggleActive(rule)"
                                />
                            </td>
                            <td
                                class="sellops-rules__td sellops-rules__td--actions"
                            >
                                <button
                                    class="sellops-rules__btn-order"
                                    @click="moveUp(index)"
                                    :disabled="index === 0"
                                >↑</button>
                                <button
                                    class="sellops-rules__btn-order"
                                    @click="moveDown(index)"
                                    :disabled="index === rules.length - 1"
                                >↓</button>
                                <button
                                    class="sellops-rules__btn-edit"
                                    @click="openEditModal(rule)"
                                >
                                    {{ $tc('sellops.general.edit') }}
                                </button>
                                <button
                                    class="sellops-rules__btn-delete"
                                    @click="deleteRule(rule)"
                                >
                                    {{ $tc('sellops.general.delete') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- ── Modal ──────────────────────────────────────────────────────
                 Flex column layout: fixed header + scrollable body + fixed footer.
                 `overflow-anchor: none` on the body prevents Chrome's scroll-anchoring
                 from jumping the scroll position when v-if chips materialise above the
                 focused input (see detailed comment in body div below). -->
            <div
                v-if="showModal"
                class="sellops-rules__modal-overlay"
            >
                <div
                    class="sellops-rules__modal"
                    @click.stop
                >
                    <!-- Header (fixed) -->
                    <div class="sellops-rules__modal-header">
                        <h3 class="sellops-rules__modal-title">
                            {{ editingRule.id ? $tc('sellops.rules.modalEditTitle') : $tc('sellops.rules.modalNewTitle') }}
                        </h3>
                        <button
                            class="sellops-rules__modal-close"
                            @click="closeModal"
                        >×</button>
                    </div>
                    <!-- Body (scrolls internally).
                         `overflow-anchor: none` prevents Chrome's scroll-anchoring from
                         jumping the modal body when v-if chip wrappers materialise above
                         the currently focused search input. -->
                    <div class="sellops-rules__modal-body">
                        <!-- Name -->
                        <div class="sellops-rules__field-group">
                            <label class="sellops-rules__field-label">
                                {{ $tc('sellops.rules.fieldName') }}
                                <span
                                    class="sellops-help sellops-help--below sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.name')"
                                    ></span>
                                </span>
                            </label>
                            <input
                                type="text"
                                class="sellops-rules__input"
                                v-model="editingRule.name"
                                :placeholder="$tc('sellops.rules.fieldNamePlaceholder')"
                            />
                        </div>
                        <!-- Priority -->
                        <div class="sellops-rules__field-group">
                            <label class="sellops-rules__field-label">
                                {{ $tc('sellops.rules.fieldPriority') }}
                                <span
                                    class="sellops-help sellops-help--below sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.priority')"
                                    ></span>
                                </span>
                            </label>
                            <input
                                type="number"
                                v-model.number="editingRule.priority"
                                min="0"
                                step="1"
                                :class="['sellops-rules__input', 'sellops-rules__input--sm', validationErrors.priority ? 'sellops-rules__input--error' : '']"
                            />
                            <div
                                v-if="validationErrors.priority"
                                class="sellops-rules__error-msg"
                            >
                                {{ validationErrors.priority }}
                            </div>
                        </div>
                        <!-- Sales channels -->
                        <div class="sellops-rules__field-group">
                            <label class="sellops-rules__field-label">
                                {{ $tc('sellops.rules.fieldSalesChannel') }}
                                <span
                                    class="sellops-help sellops-help--below sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.salesChannel')"
                                    ></span>
                                </span>
                            </label>
                            <div
                                class="sellops-rules__picker-wrap"
                                :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.sales_channel_ids || []).length }"
                            >
                                <div
                                    v-if="search.salesChannels.open && search.salesChannels.results.length"
                                    class="sellops-rules__dropdown"
                                >
                                    <div
                                        v-for="item in search.salesChannels.results"
                                        :key="item.id"
                                        :class="['sellops-rules__dropdown-item', { 'sellops-rules__dropdown-item--selected': (editingRule.sales_channel_ids || []).includes(item.id) }]"
                                        @mousedown.prevent="(editingRule.sales_channel_ids || []).includes(item.id) ? null : selectEntity('salesChannels', 'sales_channel_ids', item.id, item.name)"
                                    >
                                        <span>{{ item.name }}</span>
                                        <span
                                            v-if="(editingRule.sales_channel_ids || []).includes(item.id)"
                                            class="sellops-rules__dropdown-check"
                                        >✓</span>
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    ref="salesChannelsInput"
                                    class="sellops-rules__input"
                                    :value="search.salesChannels.query"
                                    @focus="onFocusSalesChannels"
                                    @click="onFocusSalesChannels"
                                    @blur="onBlur('salesChannels')"
                                    @input="onSalesChannelSearchInput($event.target.value)"
                                    :placeholder="$tc('sellops.rules.salesChannelPlaceholder')"
                                />
                            </div>
                            <div
                                v-if="(editingRule.sales_channel_ids || []).length"
                                class="sellops-rules__chips"
                            >
                                <span
                                    v-for="id in editingRule.sales_channel_ids"
                                    :key="id"
                                    class="sellops-rules__chip sellops-rules__chip--blue"
                                >
                                    {{ salesChannelName(id) }}
                                    <span
                                        class="sellops-rules__chip-remove"
                                        @click="removeEntity('sales_channel_ids', id)"
                                    >×</span>
                                </span>
                            </div>
                            <div
                                v-else
                                class="sellops-rules__hint-msg-sm"
                            >
                                {{ $tc('sellops.rules.salesChannelAllHint') }}
                            </div>
                        </div>
                        <!-- ── TRIGGER ─────────────────────────────────────── -->
                        <div class="sellops-rules__section-card">
                            <div class="sellops-rules__section-title">
                                {{ $tc('sellops.rules.triggerSectionTitle') }}
                            </div>
                            <label class="sellops-rules__field-label">
                                {{ $tc('sellops.rules.triggerTypeLabel') }}
                                <span
                                    class="sellops-help sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.triggerType')"
                                    ></span>
                                </span>
                            </label>
                            <select
                                class="sellops-rules__select"
                                v-model="editingRule.trigger_type"
                            >
                                <option value="any">
                                    {{ $tc('sellops.rules.triggerTypeAny') }}
                                </option>
                                <option value="products">
                                    {{ $tc('sellops.rules.triggerTypeProducts') }}
                                </option>
                                <option value="categories">
                                    {{ $tc('sellops.rules.triggerTypeCategories') }}
                                </option>
                                <option value="tags">
                                    {{ $tc('sellops.rules.triggerTypeTags') }}
                                </option>
                                <option value="product_stream">
                                    {{ $tc('sellops.rules.triggerTypeStream') }}
                                </option>
                            </select>
                            <!-- Products picker — inverted layout (dropdown opens upward) -->
                            <div v-if="editingRule.trigger_type === 'products'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.triggerProductsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.triggerProducts')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.trigger_product_ids || []).length }"
                                >
                                    <div
                                        v-if="search.triggerProducts.open && (search.triggerProducts.loading || search.triggerProducts.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.triggerProducts.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.triggerProducts.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', { 'sellops-rules__dropdown-item--selected': (editingRule.trigger_product_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.trigger_product_ids || []).includes(item.id) ? null : selectEntity('triggerProducts', 'trigger_product_ids', item.id, item.name)"
                                        >
                                            <div
                                                class="sellops-rules__dropdown-name"
                                            >
                                                <span>{{ item.name }}</span>
                                                <span
                                                    v-if="(editingRule.trigger_product_ids || []).includes(item.id)"
                                                    class="sellops-rules__dropdown-check"
                                                >✓</span>
                                            </div>
                                            <div
                                                v-if="item.productNumber || item.ean"
                                                class="sellops-rules__dropdown-meta"
                                            >
                                                <span v-if="item.productNumber">SKU: {{ item.productNumber }}</span>
                                                <span
                                                    v-if="item.productNumber && item.ean"
                                                > · </span>
                                                <span v-if="item.ean">EAN: {{ item.ean }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="triggerProductsInput"
                                        class="sellops-rules__input"
                                        :value="search.triggerProducts.query"
                                        @focus="onFocus('triggerProducts', 'product')"
                                        @click="onFocus('triggerProducts', 'product')"
                                        @blur="onBlur('triggerProducts')"
                                        @input="search.triggerProducts.query = $event.target.value; onSearchInput('triggerProducts', 'product')"
                                        :placeholder="$tc('sellops.rules.triggerProductsPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.trigger_product_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.trigger_product_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--blue"
                                    >
                                        {{ getLabel('triggerProducts', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('trigger_product_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                            <!-- Categories picker -->
                            <div v-if="editingRule.trigger_type === 'categories'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.triggerCategoriesLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.triggerCategories')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.trigger_category_ids || []).length }"
                                >
                                    <div
                                        v-if="search.triggerCategories.open && (search.triggerCategories.loading || search.triggerCategories.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.triggerCategories.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.triggerCategories.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', { 'sellops-rules__dropdown-item--selected': (editingRule.trigger_category_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.trigger_category_ids || []).includes(item.id) ? null : selectEntity('triggerCategories', 'trigger_category_ids', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.trigger_category_ids || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="triggerCategoriesInput"
                                        class="sellops-rules__input"
                                        :value="search.triggerCategories.query"
                                        @focus="onFocus('triggerCategories', 'category')"
                                        @click="onFocus('triggerCategories', 'category')"
                                        @blur="onBlur('triggerCategories')"
                                        @input="search.triggerCategories.query = $event.target.value; onSearchInput('triggerCategories', 'category')"
                                        :placeholder="$tc('sellops.rules.triggerCategoriesPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.trigger_category_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.trigger_category_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--blue"
                                    >
                                        {{ getLabel('triggerCategories', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('trigger_category_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                            <!-- Product streams picker (trigger) -->
                            <div
                                v-if="editingRule.trigger_type === 'product_stream'"
                            >
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.triggerStreamsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.triggerStream')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.trigger_product_stream_ids || []).length }"
                                >
                                    <div
                                        v-if="search.triggerStream.open && (search.triggerStream.loading || search.triggerStream.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.triggerStream.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.triggerStream.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--offer', { 'sellops-rules__dropdown-item--selected': (editingRule.trigger_product_stream_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.trigger_product_stream_ids || []).includes(item.id) ? null : selectEntity('triggerStream', 'trigger_product_stream_ids', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.trigger_product_stream_ids || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="triggerStreamInput"
                                        class="sellops-rules__input"
                                        :value="search.triggerStream.query"
                                        @focus="onFocus('triggerStream', 'product-stream')"
                                        @click="onFocus('triggerStream', 'product-stream')"
                                        @blur="onBlur('triggerStream')"
                                        @input="search.triggerStream.query = $event.target.value; onSearchInput('triggerStream', 'product-stream')"
                                        :placeholder="$tc('sellops.rules.triggerStreamsPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.trigger_product_stream_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.trigger_product_stream_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--green"
                                    >
                                        {{ getLabel('triggerStream', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('trigger_product_stream_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                            <!-- Tags picker (trigger) -->
                            <div v-if="editingRule.trigger_type === 'tags'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.triggerTagsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.triggerTags')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.trigger_tags || []).length }"
                                >
                                    <div
                                        v-if="search.triggerTags.open && (search.triggerTags.loading || search.triggerTags.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.triggerTags.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.triggerTags.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--tag', { 'sellops-rules__dropdown-item--selected': (editingRule.trigger_tags || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.trigger_tags || []).includes(item.id) ? null : selectEntity('triggerTags', 'trigger_tags', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.trigger_tags || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="triggerTagsInput"
                                        class="sellops-rules__input"
                                        :value="search.triggerTags.query"
                                        @focus="onFocus('triggerTags', 'tag')"
                                        @click="onFocus('triggerTags', 'tag')"
                                        @blur="onBlur('triggerTags')"
                                        @input="search.triggerTags.query = $event.target.value; onSearchInput('triggerTags', 'tag')"
                                        :placeholder="$tc('sellops.rules.triggerTagsPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.trigger_tags || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.trigger_tags"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--yellow"
                                    >
                                        {{ getLabel('triggerTags', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('trigger_tags', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!-- ── OFFER ─────────────────────────────────────────── -->
                        <div
                            class="sellops-rules__section-card sellops-rules__section-card--offer"
                        >
                            <div class="sellops-rules__section-title">
                                {{ $tc('sellops.rules.offerSectionTitle') }}
                            </div>
                            <label class="sellops-rules__field-label">
                                {{ $tc('sellops.rules.offerTypeLabel') }}
                                <span
                                    class="sellops-help sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.offerType')"
                                    ></span>
                                </span>
                            </label>
                            <select
                                class="sellops-rules__select"
                                v-model="editingRule.offer_type"
                            >
                                <option value="cross_selling">
                                    {{ $tc('sellops.rules.offerTypeCrossSelling') }}
                                </option>
                                <option value="frequently_bought_together">
                                    {{ $tc('sellops.rules.offerTypeFbt') }}
                                </option>
                                <option value="category_top_seller">
                                    {{ $tc('sellops.rules.offerTypeCategoryTop') }}
                                </option>
                                <option value="manual">
                                    {{ $tc('sellops.rules.offerTypeManual') }}
                                </option>
                                <option value="tags">
                                    {{ $tc('sellops.rules.offerTypeTags') }}
                                </option>
                                <option value="categories">
                                    {{ $tc('sellops.rules.offerTypeCategories') }}
                                </option>
                                <option value="product_stream">
                                    {{ $tc('sellops.rules.offerTypeStream') }}
                                </option>
                            </select>
                            <!-- Manual offer products picker — inverted layout -->
                            <div v-if="editingRule.offer_type === 'manual'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.offerProductsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.offerProducts')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap sellops-rules__picker-wrap--offer-products"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.offer_product_ids || []).length }"
                                >
                                    <div
                                        v-if="search.offerProducts.open && (search.offerProducts.loading || search.offerProducts.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.offerProducts.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.offerProducts.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--offer', { 'sellops-rules__dropdown-item--selected': (editingRule.offer_product_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.offer_product_ids || []).includes(item.id) ? null : selectEntity('offerProducts', 'offer_product_ids', item.id, item.name)"
                                        >
                                            <div
                                                class="sellops-rules__dropdown-name"
                                            >
                                                <span>{{ item.name }}</span>
                                                <span
                                                    v-if="(editingRule.offer_product_ids || []).includes(item.id)"
                                                    class="sellops-rules__dropdown-check"
                                                >✓</span>
                                            </div>
                                            <div
                                                v-if="item.productNumber || item.ean"
                                                class="sellops-rules__dropdown-meta"
                                            >
                                                <span v-if="item.productNumber">SKU: {{ item.productNumber }}</span>
                                                <span
                                                    v-if="item.productNumber && item.ean"
                                                > · </span>
                                                <span v-if="item.ean">EAN: {{ item.ean }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="offerProductsInput"
                                        class="sellops-rules__input"
                                        :value="search.offerProducts.query"
                                        @focus="onFocus('offerProducts', 'product')"
                                        @click="onFocus('offerProducts', 'product')"
                                        @blur="onBlur('offerProducts')"
                                        @input="search.offerProducts.query = $event.target.value; onSearchInput('offerProducts', 'product')"
                                        :placeholder="$tc('sellops.rules.offerProductsPlaceholder')"
                                    />
                                </div>
                                <!-- Product list with per-product discount inputs -->
                                <div
                                    v-if="(editingRule.offer_product_ids || []).length"
                                    class="sellops-rules__product-chips"
                                >
                                    <div
                                        v-for="id in editingRule.offer_product_ids"
                                        :key="id"
                                        class="sellops-rules__product-chip"
                                    >
                                        <span
                                            class="sellops-rules__product-chip-name"
                                        >
                                            <span
                                                class="sellops-rules__product-chip-text"
                                            >
                                                {{ getLabel('offerProducts', id) }}
                                            </span>
                                        </span>
                                        <label
                                            class="sellops-rules__product-discount-label"
                                        >
                                            {{ $tc('sellops.rules.offerProductsDiscountLabel') }}
                                            <input
                                                type="number"
                                                min="0"
                                                :max="maxDiscount"
                                                step="0.5"
                                                class="sellops-rules__product-discount-input"
                                                :placeholder="getPerProductDiscountPlaceholder()"
                                                :value="getPerProductDiscount(id)"
                                                @input="setPerProductDiscount(id, $event.target.value)"
                                            />
                                            %
                                        </label>
                                        <span
                                            class="sellops-rules__product-chip-remove"
                                            :title="$tc('sellops.rules.offerProductsRemoveTitle')"
                                            @click="removeEntity('offer_product_ids', id); removePerProductDiscount(id);"
                                        >×</span>
                                    </div>
                                    <div
                                        v-if="validationErrors.offer_product_discounts"
                                        class="sellops-rules__product-chips-validation"
                                    >
                                        {{ validationErrors.offer_product_discounts }}
                                    </div>
                                    <div
                                        v-else
                                        class="sellops-rules__product-chips-hint"
                                    >
                                        {{ $tc('sellops.rules.offerProductsHint') }}
                                        <strong>0</strong>
                                        {{ $tc('sellops.rules.offerProductsHint2') }}
                                    </div>
                                </div>
                            </div>
                            <!-- Offer-by-tag picker -->
                            <div v-if="editingRule.offer_type === 'tags'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.offerTagsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.offerTags')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.offer_tag_ids || []).length }"
                                >
                                    <div
                                        v-if="search.offerTags.open && (search.offerTags.loading || search.offerTags.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.offerTags.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.offerTags.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--tag', { 'sellops-rules__dropdown-item--selected': (editingRule.offer_tag_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.offer_tag_ids || []).includes(item.id) ? null : selectEntity('offerTags', 'offer_tag_ids', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.offer_tag_ids || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="offerTagsInput"
                                        class="sellops-rules__input"
                                        :value="search.offerTags.query"
                                        @focus="onFocus('offerTags', 'tag')"
                                        @click="onFocus('offerTags', 'tag')"
                                        @blur="onBlur('offerTags')"
                                        @input="search.offerTags.query = $event.target.value; onSearchInput('offerTags', 'tag')"
                                        :placeholder="$tc('sellops.rules.offerTagsPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.offer_tag_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.offer_tag_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--yellow"
                                    >
                                        {{ getLabel('offerTags', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('offer_tag_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                            <!-- Offer-by-categories picker -->
                            <div v-if="editingRule.offer_type === 'categories'">
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.offerCategoriesLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.offerCategories')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.offer_category_ids || []).length }"
                                >
                                    <div
                                        v-if="search.offerCategories.open && (search.offerCategories.loading || search.offerCategories.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.offerCategories.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.offerCategories.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--offer', { 'sellops-rules__dropdown-item--selected': (editingRule.offer_category_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.offer_category_ids || []).includes(item.id) ? null : selectEntity('offerCategories', 'offer_category_ids', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.offer_category_ids || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="offerCategoriesInput"
                                        class="sellops-rules__input"
                                        :value="search.offerCategories.query"
                                        @focus="onFocus('offerCategories', 'category')"
                                        @click="onFocus('offerCategories', 'category')"
                                        @blur="onBlur('offerCategories')"
                                        @input="search.offerCategories.query = $event.target.value; onSearchInput('offerCategories', 'category')"
                                        :placeholder="$tc('sellops.rules.offerCategoriesPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.offer_category_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.offer_category_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--green"
                                    >
                                        {{ getLabel('offerCategories', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('offer_category_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                            <!-- Product streams picker (offer) -->
                            <div
                                v-if="editingRule.offer_type === 'product_stream'"
                            >
                                <label class="sellops-rules__picker-label">
                                    {{ $tc('sellops.rules.offerStreamsLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.offerStream')"
                                        ></span>
                                    </span>
                                </label>
                                <div
                                    class="sellops-rules__picker-wrap"
                                    :class="{ 'sellops-rules__picker-wrap--has-selection': (editingRule.offer_product_stream_ids || []).length }"
                                >
                                    <div
                                        v-if="search.offerStream.open && (search.offerStream.loading || search.offerStream.results.length)"
                                        class="sellops-rules__dropdown"
                                    >
                                        <div
                                            v-if="search.offerStream.loading"
                                            class="sellops-rules__dropdown-loading"
                                        >
                                            {{ $tc('sellops.general.loading') }}
                                        </div>
                                        <div
                                            v-for="item in search.offerStream.results"
                                            :key="item.id"
                                            :class="['sellops-rules__dropdown-item', 'sellops-rules__dropdown-item--offer', { 'sellops-rules__dropdown-item--selected': (editingRule.offer_product_stream_ids || []).includes(item.id) }]"
                                            @mousedown.prevent="(editingRule.offer_product_stream_ids || []).includes(item.id) ? null : selectEntity('offerStream', 'offer_product_stream_ids', item.id, item.name)"
                                        >
                                            <span>{{ item.name }}</span>
                                            <span
                                                v-if="(editingRule.offer_product_stream_ids || []).includes(item.id)"
                                                class="sellops-rules__dropdown-check"
                                            >✓</span>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        ref="offerStreamInput"
                                        class="sellops-rules__input"
                                        :value="search.offerStream.query"
                                        @focus="onFocus('offerStream', 'product-stream')"
                                        @click="onFocus('offerStream', 'product-stream')"
                                        @blur="onBlur('offerStream')"
                                        @input="search.offerStream.query = $event.target.value; onSearchInput('offerStream', 'product-stream')"
                                        :placeholder="$tc('sellops.rules.offerStreamsPlaceholder')"
                                    />
                                </div>
                                <div
                                    v-if="(editingRule.offer_product_stream_ids || []).length"
                                    class="sellops-rules__chips"
                                >
                                    <span
                                        v-for="id in editingRule.offer_product_stream_ids"
                                        :key="id"
                                        class="sellops-rules__chip sellops-rules__chip--green"
                                    >
                                        {{ getLabel('offerStream', id) }}
                                        <span
                                            class="sellops-rules__chip-remove"
                                            @click="removeEntity('offer_product_stream_ids', id)"
                                        >×</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!-- Discount & Max offers -->
                        <div
                            :class="['sellops-rules__offer-grid', editingRule.offer_type === 'manual' ? 'sellops-rules__offer-grid--1col' : 'sellops-rules__offer-grid--2col']"
                        >
                            <div v-if="editingRule.offer_type !== 'manual'">
                                <label class="sellops-rules__field-label">
                                    {{ $tc('sellops.rules.discountLabel') }}
                                    <span
                                        class="sellops-help sellops-help--align-left"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.discount')"
                                        ></span>
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    v-model.number="editingRule.discount_percent"
                                    min="0"
                                    :max="maxDiscount"
                                    step="0.5"
                                    :class="['sellops-rules__input', validationErrors.discount_percent ? 'sellops-rules__input--error' : '']"
                                />
                                <div
                                    v-if="validationErrors.discount_percent"
                                    class="sellops-rules__error-msg"
                                >
                                    {{ validationErrors.discount_percent }}
                                </div>
                                <div
                                    v-else
                                    class="sellops-rules__hint-msg"
                                >
                                    {{ $tc('sellops.rules.discountHint') }}
                                </div>
                            </div>
                            <div>
                                <label class="sellops-rules__field-label">
                                    {{ $tc('sellops.rules.maxOffersLabel') }}
                                    <span
                                        :class="['sellops-help', editingRule.offer_type === 'manual' ? 'sellops-help--align-left' : 'sellops-help--align-right']"
                                        tabindex="0"
                                    >
                                        ?
                                        <span
                                            class="sellops-help__tip"
                                            v-html="$tc('sellops.rules.tooltips.maxOffers')"
                                        ></span>
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    v-model.number="editingRule.max_offers"
                                    min="1"
                                    max="12"
                                    class="sellops-rules__input"
                                />
                            </div>
                        </div>
                        <!-- Active -->
                        <div class="sellops-rules__active-row">
                            <input
                                type="checkbox"
                                v-model="editingRule.active"
                                id="sellops-rule-active-cb"
                                class="sellops-rules__toggle"
                            />
                            <label
                                for="sellops-rule-active-cb"
                                class="sellops-rules__active-label"
                            >
                                {{ $tc('sellops.rules.activeLabel') }}
                                <span
                                    class="sellops-help sellops-help--align-left"
                                    tabindex="0"
                                >
                                    ?
                                    <span
                                        class="sellops-help__tip"
                                        v-html="$tc('sellops.rules.tooltips.active')"
                                    ></span>
                                </span>
                            </label>
                        </div>
                    </div>
                    <!-- Footer (fixed) -->
                    <div class="sellops-rules__modal-footer">
                        <button
                            class="sellops-rules__btn-cancel"
                            @click="closeModal"
                        >
                            {{ $tc('sellops.general.cancel') }}
                        </button>
                        <button
                            class="sellops-rules__btn-save"
                            @click="saveRule"
                            :disabled="isSaving"
                        >
                            {{ isSaving ? $tc('sellops.rules.saving') : $tc('sellops.rules.saveRule') }}
                        </button>
                    </div>
                </div>
            </div>
            <!-- ── Delete confirmation modal ── -->
            <sw-confirm-modal
                v-if="ruleToDelete !== null"
                :text="$tc('sellops.rules.errors.deleteConfirm')"
                @confirm="confirmDeleteRule"
                @cancel="cancelDeleteRule"
            />
        </div>
    </template>
    {% endblock %}
</sw-page>
{% endblock %}